import { useEffect, useState } from 'react';
import type { Field } from '../../../../types';
import { addField, addSeason, areFieldsLoaded } from '../../../../store';
import { showToast } from '../../../../components/base/Toast';
import { DocumentUploadSlot, type ApplyExtracted } from '../../../../components/feature/DocumentUploadSlot';
import { CropSelect } from '../../../../components/feature/CropSelect';
import { useAuth } from '../../../../hooks/useAuth';
import { useCrops } from '../../../../hooks/useCrops';
import { fieldNumber, fieldString, seasonCropFromContract } from '../../../../api/aiExtraction';
import { matchCatalogName } from '../../../../utils/catalogMatch';
import { ClearableInput, ClearableTextarea, FieldError } from '../../../../components/base/ClearableInput';
import { isoDate, positive, validate } from '../../../../utils/validators';

/** HTX creating a Field and/or Season on behalf of one of its farmers — the
 * farmer-side app already has this (FieldTab/SeasonTab); this is the same
 * backend routes (already open to HTX_STAFF), just a missing HTX-side entry
 * point. If the farmer has no field yet, one is created inline first so
 * "Tạo mùa vụ" is never blocked on a separate trip to another screen.
 *
 * Both the new field and the season itself can be filled in two ways: type
 * the values directly, or attach a document (sổ đỏ/GCN đất for the field,
 * hợp đồng bao tiêu for the season) and let AI read it and fill the empty
 * fields — never the only way, since a document may not exist yet or the
 * reading may fail; the manual inputs stay live either way. */
export function CreateSeasonModal({
  farmerId,
  fields,
  onClose,
  onCreated,
}: {
  farmerId: string;
  fields: Field[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [fieldId, setFieldId] = useState(fields[0]?.id || '');
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldArea, setNewFieldArea] = useState('');
  const [newFieldLocation, setNewFieldLocation] = useState('');
  const [newFieldCode, setNewFieldCode] = useState('');
  const [newFieldNotes, setNewFieldNotes] = useState('');
  // The land document read to fill the new plot is kept on it, not thrown away.
  const [newFieldDocIds, setNewFieldDocIds] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [mainCrop, setMainCrop] = useState('');
  const [cropId, setCropId] = useState<string | undefined>();
  const { user } = useAuth();
  const crops = useCrops(user?.htxId);
  const [startDate, setStartDate] = useState('');
  const [expectedYield, setExpectedYield] = useState('');
  const [unit, setUnit] = useState('kg');
  const [submitting, setSubmitting] = useState(false);

  // A farmer's existing plots are always offered as a pick list; a new plot is
  // created only when there is none, or when the HTX picks "+ Tạo thửa đất
  // mới". The list used to be read before it had loaded, so an empty
  // still-loading list showed the "chưa có thửa đất" form even for a farmer
  // who already had plots.
  // Sticky once true: a background refetch (another tab's change) must not
  // swap the form back to "đang tải" and unmount an upload in progress.
  const loadedNow = areFieldsLoaded(farmerId);
  const [everLoaded, setEverLoaded] = useState(loadedNow);
  useEffect(() => {
    if (loadedNow) setEverLoaded(true);
  }, [loadedNow]);
  const fieldsLoaded = loadedNow || everLoaded;
  const NEW_FIELD = '__new__';
  const needsNewField = fieldsLoaded && (fields.length === 0 || fieldId === NEW_FIELD);
  useEffect(() => {
    if (!fieldId && fields.length > 0) setFieldId(fields[0].id);
  }, [fields, fieldId]);

  // Each setter only replaces what is still empty, unless the HTX asked to overwrite.
  const fill = (overwrite: boolean, current: string, next: string | undefined, set: (v: string) => void) => {
    if (next !== undefined && (overwrite || !current.trim())) set(next);
  };

  const applyLandTitle: ApplyExtracted = (f, { overwrite }) => {
    fill(overwrite, newFieldName, fieldString(f.name), setNewFieldName);
    fill(overwrite, newFieldCode, fieldString(f.code), setNewFieldCode);
    fill(overwrite, newFieldArea, fieldNumber(f.areaHa)?.toString(), setNewFieldArea);
    fill(overwrite, newFieldLocation, fieldString(f.address), setNewFieldLocation);
    fill(overwrite, newFieldNotes, fieldString(f.description), setNewFieldNotes);
  };

  const applyOfftake: ApplyExtracted = (f, { overwrite }) => {
    // Only a crop in the catalog can be picked; anything else is left for the HTX to choose.
    // The crop line this season grows, with its own quantity — not the
    // contract's total across every crop.
    const { crop: cropName, outputKg: output } = seasonCropFromContract(f, (crops ?? []).map((c) => c.name), matchCatalogName);
    if (cropName && (overwrite || !mainCrop)) {
      setMainCrop(cropName);
      setCropId(crops?.find((c) => c.name === cropName)?._id);
    }
    if (output !== undefined && (overwrite || !expectedYield)) {
      setExpectedYield(String(output));
      setUnit('kg');
    }
  };

  // Errors follow the values: an AI-filled value that is wrong shows red at
  // once, an empty required field once "Tạo mùa vụ" was pressed.
  const [submitted, setSubmitted] = useState(false);
  const allErrors = {
    newFieldName: needsNewField ? validate(newFieldName, [], true, 'tên thửa đất') : undefined,
    newFieldArea: needsNewField ? validate(newFieldArea, [positive('Diện tích', 10_000)]) : undefined,
    name: validate(name, [], true, 'tên mùa vụ'),
    mainCrop: mainCrop ? undefined : 'Vui lòng chọn cây trồng',
    startDate: validate(startDate, [isoDate], true, 'ngày bắt đầu'),
    expectedYield: validate(expectedYield, [positive('Sản lượng', 100_000_000)], true, 'sản lượng dự kiến'),
    unit: validate(unit, [], true, 'đơn vị'),
  };
  const err = (k: keyof typeof allErrors, value: string) => (value.trim() || submitted ? allErrors[k] : undefined);

  async function submit() {
    if (!fieldsLoaded) return showToast('Đang tải danh sách thửa đất, vui lòng chờ', 'warning');
    setSubmitted(true);
    if (!needsNewField && !fieldId) return showToast('Chọn thửa đất', 'error');
    if (Object.values(allErrors).some(Boolean)) return showToast('Vui lòng sửa các ô viền đỏ', 'warning');
    setSubmitting(true);
    try {
      let targetFieldId = fieldId;
      if (needsNewField) {
        const field = await addField({
          farmerId,
          maThuaDat: newFieldCode.trim(),
          name: newFieldName.trim(),
          area: newFieldArea ? Number(newFieldArea) : 0,
          location: newFieldLocation,
          ghiChu: newFieldNotes.trim() || undefined,
          loaiDat: '',
          status: 'Đang canh tác',
          taiLieu: newFieldDocIds,
        });
        targetFieldId = field.id;
      }
      await addSeason({
        farmerId,
        fieldId: targetFieldId,
        maMuaVu: '',
        name: name.trim(),
        cayTrong: mainCrop,
        cropId,
        variety: '',
        startDate,
        expectedYield: Number(expectedYield),
        unit,
        status: 'Chuẩn bị',
      });
      showToast('Đã tạo mùa vụ', 'success');
      onCreated();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Tạo mùa vụ thất bại', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white p-4 space-y-3 max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Tạo mùa vụ</h3>
          <button type="button" onClick={onClose} className="text-gray-400"><i className="ri-close-line text-lg" /></button>
        </div>

        {!fieldsLoaded ? (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Thửa đất</label>
            <select disabled className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-gray-50">
              <option>Đang tải thửa đất của nông hộ...</option>
            </select>
          </div>
        ) : (
          <>
            {fields.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Thửa đất</label>
                <select value={fieldId} onChange={(e) => setFieldId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white">
                  {fields.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.area} ha)</option>)}
                  <option value={NEW_FIELD}>+ Tạo thửa đất mới</option>
                </select>
              </div>
            )}
            {needsNewField && (
          <div className="rounded-lg bg-sky-50 border border-sky-200 p-2.5 space-y-2">
            <p className="text-[11px] text-sky-800">
              {fields.length === 0 ? 'Nông hộ chưa có thửa đất — tạo nhanh một thửa để gắn mùa vụ này:' : 'Tạo thửa đất mới cho nông hộ:'}
            </p>
            <DocumentUploadSlot
              label="Giấy chứng nhận thửa đất (không bắt buộc)"
              hint="Tải ảnh sổ đỏ/GCN để AI tự điền tên, mã, địa chỉ, diện tích và mô tả thửa — vẫn có thể tự nhập."
              docType="LAND_TITLE"
              purpose="LAND_TITLE_DOCUMENT"
              onUploaded={(fileId) => setNewFieldDocIds([fileId])}
              onExtracted={applyLandTitle}
            />
            <div className="grid grid-cols-2 gap-2">
              <ClearableInput value={newFieldName} onValueChange={setNewFieldName} placeholder="Tên thửa đất *" error={err('newFieldName', newFieldName)} />
              <ClearableInput value={newFieldCode} onValueChange={setNewFieldCode} placeholder="Mã thửa (nếu có)" />
              <ClearableInput type="number" value={newFieldArea} onValueChange={setNewFieldArea} placeholder="Diện tích (ha)" error={err('newFieldArea', newFieldArea)} />
              <ClearableInput value={newFieldLocation} onValueChange={setNewFieldLocation} placeholder="Vị trí/địa chỉ" />
            </div>
            <ClearableTextarea value={newFieldNotes} onValueChange={setNewFieldNotes} rows={2} maxLength={500} placeholder="Mô tả thửa (loại đất, số GCN...)" />
          </div>
            )}
          </>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tên mùa vụ</label>
          <ClearableInput value={name} onValueChange={setName} placeholder="VD: Vụ Đông Xuân 2026" error={err('name', name)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cây trồng</label>
            <CropSelect value={mainCrop} htxId={user?.htxId} invalid={submitted && Boolean(allErrors.mainCrop)} onChange={(name, crop) => { setMainCrop(name); setCropId(crop?._id); }} />
            <FieldError message={submitted ? allErrors.mainCrop : undefined} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ngày bắt đầu</label>
            <ClearableInput type="date" value={startDate} onValueChange={setStartDate} error={err('startDate', startDate)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sản lượng dự kiến</label>
            <ClearableInput type="number" value={expectedYield} onValueChange={setExpectedYield} error={err('expectedYield', expectedYield)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Đơn vị</label>
            <ClearableInput value={unit} onValueChange={setUnit} error={err('unit', unit)} />
          </div>
        </div>

        <DocumentUploadSlot
          label="Hợp đồng bao tiêu (không bắt buộc)"
          hint="Tải ảnh/PDF hợp đồng để AI tự điền cây trồng và sản lượng dự kiến — tệp chỉ dùng để đọc, không lưu vào mùa vụ."
          docType="OFFTAKE_CONTRACT"
          purpose="CONTRACT_DOCUMENT"
          onExtracted={applyOfftake}
        />

        <button type="button" disabled={submitting} onClick={submit} className="w-full rounded-lg bg-agrida-600 text-white text-sm font-semibold py-2.5 disabled:opacity-50">
          {submitting ? 'Đang tạo...' : 'Tạo mùa vụ'}
        </button>
      </div>
    </div>
  );
}
